import type { Instant } from '@taler/domain'
import { compareInstant } from '@taler/domain'
import type { ApiRequest } from './transport.ts'

export interface AccessToken {
  readonly value: string
  readonly expiresAt: Instant
}

/**
 * Where a token comes from. The sign-in flow that produces one is the app's business;
 * the client only ever asks for a current token and, on a rejection, for a fresh one.
 * A second token source is a different provider passed at construction.
 */
export interface TokenProvider {
  /** A token believed valid, or null when the user must sign in. */
  current(): Promise<AccessToken | null>
  /** Obtains a fresh token. Null when renewal is impossible and sign-in is required. */
  refresh(): Promise<AccessToken | null>
  signOut(): void
}

export interface AuthStrategy {
  prepare(request: ApiRequest): Promise<ApiRequest>
  /** Called once when the server rejects a token. Returns false when sign-in is required. */
  renew(): Promise<boolean>
  signOut(): void
}

export function bearerAuth(provider: TokenProvider): AuthStrategy {
  return {
    async prepare(request) {
      const token = await provider.current()
      if (token === null) return request
      return { ...request, headers: { ...request.headers, authorization: `Bearer ${token.value}` } }
    },
    async renew() {
      return (await provider.refresh()) !== null
    },
    signOut: () => provider.signOut(),
  }
}

/** For the mock, for e2e, and for a deployment that authenticates below the client. */
export const anonymousAuth: AuthStrategy = {
  prepare: async (request) => request,
  renew: async () => false,
  signOut: () => {},
}

/** A token is stale before it expires: clocks disagree, and a request in flight takes time. */
export function isExpired(token: AccessToken, now: Instant, skewSeconds = 60n): boolean {
  return (
    token.expiresAt.epochSecond - now.epochSecond <= skewSeconds || compareInstant(token.expiresAt, now) <= 0
  )
}
